import React, { useEffect, useRef } from "react";
import { ChatMessage } from "./ChatMessage";
import { cn } from "../../lib/utils";

export interface ChatMessageData {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface ChatWindowProps {
  messages: ChatMessageData[];
  isLoading?: boolean;
  className?: string;
}

export function ChatWindow({ messages, isLoading, className }: ChatWindowProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto p-4 space-y-2",
        "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
        className
      )}
    >
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 px-4">
          <p className="text-sm mb-2 font-medium">👋 Hello! How can I help you today?</p>
          <p className="text-xs mb-4">Ask me about your clients, search for information, or get assistance.</p>
          <div className="text-xs space-y-1 text-gray-400 mb-3">
            <p className="font-medium">Try asking:</p>
            <p>"Find me a client named John Smith"</p>
            <p>"Show me all my clients"</p>
            <p>"What's the overview of my client portfolio?"</p>
          </div>
          <div className="text-xs text-gray-400 border-t pt-3 mt-3">
            <p className="font-medium text-gray-500">ℹ️ Note:</p>
            <p>Each message starts fresh. Ask complete questions for best results.</p>
          </div>
        </div>
      ) : (
        <>
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              role={message.role}
              content={message.content}
              timestamp={message.timestamp}
            />
          ))}
          {isLoading && (
            <div className="flex gap-3 mb-4">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <div className="h-4 w-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg rounded-bl-none px-4 py-2.5">
                <div className="flex gap-1">
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

